import type { Cell, Rate, SuppressedBuckets } from "~/lib/suppression";
import type { EventFigureCells } from "~/lib/event-figure";
import type { ActivityCells } from "~/lib/app-use";
import type { QuestionCells } from "~/lib/question-figures";
import type { CapabilityManifest } from "./capabilities";
import type { CohortKind, CohortRecord } from "./cohorts";
import type { CapacityBand } from "./rate-card";
import type { Role } from "./sessions";

export type { CohortKind, CohortRecord };

/**
 * The wire contract between this Worker and the enterprise React app.
 *
 * EVERY FIGURE IS A `Cell`. That is the point of the type, and it is what
 * makes the suppression commitment structural rather than procedural: a raw
 * number has no field to sit in. If a panel finds itself unwrapping a Cell
 * to get at a number, that is the bug, not the type.
 *
 * Four states must never be conflated, and each is a distinct shape here:
 *   suppressed   we know it and may not say it
 *   unavailable  we could not ask (a source is down or unconfigured)
 *   value 0      a real measurement of nothing
 *   notMeasured  P3 does not collect this at all
 * Zero and not-measured look identical on a chart and mean opposite things.
 */

export interface SourceReport {
  readonly state: "ok" | "unavailable" | "notMeasured";
  readonly reason?: string;
  /** True when the pull hit its page cap, so the figure is a floor. */
  readonly truncated?: boolean;
}

export interface RateFigure {
  readonly cell: Cell<Rate>;
  /** Adjusted-Wald 95% interval. Absent when the rate itself is not published. */
  readonly ci95: readonly [number, number] | null;
  /** True below a denominator of 30: the figure reads as a direction, not a level. */
  readonly directional: boolean;
}

export interface WeeklyPoint {
  /** The Sunday the week ends on, 'YYYY-MM-DD'. Sunday-anchored, always. */
  readonly weekEnding: string;
  readonly cumulative: Cell<number>;
}

export interface WeeklySeries {
  readonly anchor: "sunday";
  /** The current partial week is excluded, never drawn as a drop. */
  readonly partialWeekExcluded: true;
  readonly points: readonly WeeklyPoint[];
}

export type Breakdown = SuppressedBuckets;

/**
 * A count of events and the students behind it, from `eventFigure`
 * (`src/lib/event-figure.ts`): the total needs ten students behind it, and
 * the student count carries the complement rule.
 */
export type EventFigure = EventFigureCells;

/**
 * The Milestones section (Sep 11 2026). Completions are events, so their
 * total carries the students behind it (`EventFigure`); top milestones and
 * outcomes count students, one per milestone; the weekly curve is a running
 * total of completions, a week publishing only with ten students behind it;
 * progress places every claimed student by completions. Completions per
 * student are derived by the app from the total and the student count, and
 * never sent: a ratio is not a count of people, and the guard cannot tell.
 */
export interface MilestonesSection {
  readonly completed: EventFigure;
  readonly top: Breakdown;
  readonly weekly: WeeklySeries;
  readonly progress: Breakdown;
  readonly outcomes: Breakdown;
  readonly outcomeStudents: Cell<number>;
  /**
   * Field match (Sep 12 2026): of the students whose first full-time role can
   * be compared with the industry on their own profile, how many are working
   * in that field. The role's industry is classified inside
   * `p3-milestones-reader`, the only Worker allowed to read the note it comes
   * from; a label crosses the binding, never the text.
   *
   * The denominator is the COMPARABLE students, not the cohort: a student
   * with no first role on record, no profile industry, or "Other" on either
   * side is in neither part. Over the cohort this figure would measure how
   * many students have reached that milestone, which is a different question
   * and already answered by `outcomes`.
   */
  readonly fieldMatch: Cell<Rate>;
}

/**
 * App activity per institution, from the app's analytics (Sep 11 2026, Pipe
 * C): students active over the trailing 90 days, sessions per student and
 * the average session length (ratios carrying both counts), and the apply
 * clicks the live app records. A student counts only when their analytics
 * profile names the institution, so every figure is a floor; an institution
 * the analytics hold no student for reads unavailable, never zero.
 */
export type AppActivitySection = ActivityCells;

/**
 * Career Timeline and Pulse AI per institution (Sep 12 2026, Pipe D), from
 * the web app's own store through its aggregate endpoint.
 *
 * Joined by STUDENT ID, not by name, so unlike app activity this is not a
 * name-matched floor: a student counts here exactly when the institution's
 * own student index named them. `withPulse` is students with at least one
 * LIVE Pulse session, decided by the store, because a pulse-coach row can
 * hold zero sessions and a row count would overstate it.
 */
export interface CareerTimelineSectionWire {
  readonly withTimeline: Cell<Rate>;
  readonly withPulse: Cell<Rate>;
  /** Live Pulse sessions with the students behind them. */
  readonly pulse: EventFigure;
  readonly entries: EventFigure;
  readonly byKind: Breakdown;
  readonly byStage: Breakdown;
}

/**
 * Questions per institution, from the question index (Sep 11 2026): asked
 * in the trailing 90 days, the share a mentor answered (a rate carrying both
 * counts), and a weekly running total. Questions follow the student.
 */
export type QuestionsSection = QuestionCells;

export interface MetricsPayload {
  readonly capturedAt: number;
  /** True when the snapshot is older than three hours. The header shows the real stamp either way. */
  readonly stale: boolean;
  /** A cut's figures the tick kept rather than replace (Sep 19 2026, lib/cut-guard.ts). */
  readonly held?: boolean;
  readonly org: { readonly name: string; readonly slug: string };
  readonly suppression: {
    readonly floor: number;
    readonly complementary: true;
    readonly statement: string;
  };
  readonly sections: {
    readonly reach: {
      readonly claimed: Cell<number>;
      readonly weekly: WeeklySeries;
      readonly sources: Breakdown;
    };
    readonly engagement: {
      readonly matched: RateFigure;
      readonly waitingWithRequest: Cell<number>;
      readonly wants: Breakdown;
      /**
       * Sep 10 2026, for the live dashboard. The total of confirmed
       * mentorships across the claimed students: a count of EVENTS, so it
       * publishes only when the students behind it (the matched ones) clear
       * the floor, whatever the total is. And how many each student holds.
       */
      readonly mentorshipsHeld: Cell<number>;
      readonly mentorshipCounts: Breakdown;
    };
    readonly composition: {
      readonly education: Breakdown;
      readonly industry: Breakdown;
      readonly meetFrequency: Breakdown;
    };
    /**
     * Running totals from each student's own record (Sep 11 2026): what the
     * index counts on the record, never a period. A student's totals include
     * what they did before they named the institution, and move with them if
     * they change it. Optional because a snapshot written before this field
     * existed is served until the next hourly tick replaces it.
     */
    readonly activity?: {
      readonly questions: EventFigure;
      readonly videoPlays: EventFigure;
      readonly videosSaved: EventFigure;
      readonly mentorsFollowed: EventFigure;
      /** Students with no question, video play, save, follow, mentorship or
       *  open request on record, over the claimed students. */
      readonly notYetActive: Cell<Rate>;
    };
    /**
     * Milestones per institution (Sep 11 2026), read through
     * p3-milestones-reader. Optional: absent while the reader is not bound or
     * could not be read, and on a snapshot written before this field existed.
     */
    readonly milestones?: MilestonesSection;
    /** App activity per institution (Sep 11 2026, Pipe C). Optional: absent
     *  when the analytics are not configured or were not read on the last
     *  refresh, and on a snapshot written before this field existed. */
    readonly appActivity?: AppActivitySection;
    /** Questions over time and answered (Sep 11 2026). Optional, as above. */
    readonly questionsOverTime?: QuestionsSection;
    /** Career Timeline and Pulse AI (Pipe D). Absent while the web store is
     *  unbound, was not read, or holds nothing for this institution. */
    readonly careerTimeline?: CareerTimelineSectionWire;
  };
  /**
   * Every student on P3, in aggregate, for the one comparison the Data
   * Sharing Addendum names. Computed from the same index pull, so it is as
   * fresh as the institution's own figures and never a hand-typed constant.
   */
  readonly platform: {
    readonly students: Cell<number>;
    readonly matched: RateFigure;
  };
  readonly sources: Readonly<Record<string, SourceReport>>;
  /**
   * Set on a cohort's document (Sep 17 2026): the same sections computed
   * over the cohort's members alone, with the floor applied to the cohort.
   * Absent on the institution's own document.
   */
  readonly cohort?: { readonly id: string; readonly name: string };
  /**
   * Set on a slice's document (Sep 17 2026): the same sections computed over
   * the students the platform says do, or do not, hold a mentorship, with the
   * floor applied to the SLICE. A slice is derived and nobody stores its
   * membership, which is what makes it different from a cohort, so it carries
   * its own field rather than borrowing that one.
   */
  readonly slice?: { readonly id: SliceId; readonly name: string };
}

export interface SessionPayload {
  readonly orgName: string;
  readonly orgSlug: string;
  readonly memberId: string;
  readonly memberName: string | null;
  readonly email: string;
  readonly role: Role;
  readonly accessMode: "full" | "readonly" | "export_only";
  readonly plan: {
    readonly band: CapacityBand | null;
    readonly bandLabel: string | null;
    readonly interval: "month" | "year" | null;
    readonly renewsAt: number | null;
  };
  readonly asOf: number | null;
  readonly memberships: readonly { readonly membershipId: string; readonly orgName: string }[];
  readonly capabilities: CapabilityManifest;
  /** Whether this institution has a roster of who has joined (Sep 17 2026):
   *  begun for institutions onboarded from that date, never for a historic one. */
  readonly roster: boolean;
}

/* ==========================================================================
   Who has joined (Sep 17 2026)
   ========================================================================== */

export interface RosterStudent {
  readonly id: string;
  /** First name and last initial, never the full name. */
  readonly name: string;
  readonly joinedAt: number | null;
  readonly mentor: "matched" | "waiting" | "none";
  readonly mentorships: number;
}

export interface RosterMentor {
  readonly id: string;
  readonly name: string;
  readonly title: string | null;
  /** The platform's own status: APPROVED, PENDING, and so on. */
  readonly status: string | null;
  readonly joinedAt: number | null;
  readonly mentorships: number;
  readonly capacity: number | null;
}

export interface RosterPayload {
  /** False on a historic institution: the page says so and shows counts only. */
  readonly available: boolean;
  /** When the roster began, unix seconds, or null. */
  readonly since: number | null;
  readonly refreshedAt: number | null;
  readonly students: readonly RosterStudent[];
  readonly mentors: readonly RosterMentor[];
  readonly counts: {
    readonly students: number;
    readonly matched: number;
    readonly waiting: number;
    readonly mentors: number;
  };
}

/* ==========================================================================
   Sub-cohorts (Sep 17 2026)
   ========================================================================== */

/**
 * `GET /api/enterprise/cohorts`: the institution's own groups of its
 * enrolled students, and each student's membership by platform id. The
 * name collision with `CohortPayload` below is historical: that one is the
 * claim campaign (the institution's whole cohort of students), this one is
 * the groups administrators cut it into.
 */
export interface CohortsPayload {
  /** False on a historic institution: no roster, so nothing to group. */
  readonly available: boolean;
  readonly cohorts: readonly CohortRecord[];
  /** Student platform id to cohort id, for students on Enrollment now. */
  readonly memberships: Readonly<Record<string, string>>;
  /** Active cohorts an institution may hold. */
  readonly limit: number;
  readonly kinds: readonly CohortKind[];
}

/* ==========================================================================
   Matching (Sep 17 2026)
   ========================================================================== */

export type MatchStatusWire = "PENDING" | "ACTIVE" | "INACTIVE";

/** One request the institution sent, by platform ids the roster already names. */
/** The two cuts a live tenant can take of its own students. */
export type SliceId = "matched" | "waiting";

export const SLICE_NAME: Record<SliceId, string> = {
  matched: "Students with a mentor",
  waiting: "Students waiting for a mentor",
};

export interface MatchRequestWire {
  readonly id: string;
  readonly studentId: string;
  readonly mentorId: string;
  readonly status: MatchStatusWire;
  readonly requestedAt: number;
  /** When the platform last answered for this pair. */
  readonly checkedAt: number;
  /** When an administrator withdrew it (Sep 18 2026), else null. Set only on
   *  an INACTIVE row; the screen reads "Withdrawn" rather than "Ended". */
  readonly withdrawnAt: number | null;
}

/**
 * A mentorship the platform already holds between two people on Enrollment,
 * whoever asked for it (Sep 17 2026). It is NOT a request this portal sent:
 * those are `requests`, and the audit trail answers for them. The board draws
 * these so an administrator is never offered a pair that already exists.
 */
export interface ExistingPairWire {
  readonly studentId: string;
  readonly mentorId: string;
  readonly status: "PENDING" | "ACTIVE";
  /** The platform's word for who asked, when it says. */
  readonly origin: string | null;
  /** True when this portal's own request is the reason it exists, so the
   *  board draws one rope rather than two for the same pair. */
  readonly asked: boolean;
}

export interface MatchesPayload {
  /** False while the gateway is not bound here, or the institution has no roster. */
  readonly available: boolean;
  readonly reason: string | null;
  readonly requests: readonly MatchRequestWire[];
  /** Every open mentorship between two people on Enrollment, as of the last tick. */
  readonly existing: readonly ExistingPairWire[];
  /** Requests an institution may send in an hour. */
  readonly perHour: number;
}

export interface MatchSuggestionsPayload {
  /** Mentor platform ids on the institution's roster, in the platform's order. */
  readonly mentorIds: readonly string[];
}

export interface CohortPayload {
  readonly claimed: Cell<number>;
  readonly joinCodes: readonly {
    readonly code: string;
    readonly label: string | null;
    readonly active: boolean;
    readonly retiresAt: number | null;
    readonly url: string;
  }[];
  readonly sources: Breakdown;
  readonly funnel: Breakdown;
}
